import React, { useContext, useEffect } from 'react';
import cn from 'classnames';
import Modal from '@wix/da-shared-react/pkg/Popup/Modal';
import { CoreTypes } from '../../types';
import HeroContent from './HeroContent';
import BillingCyclePicker from './BillingCyclePicker';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  ThemeOverride,
  ThemeOptions,
} from '@wix/da-react-context/pkg/ThemeContext';
import ArrowLeft from '@wix/da-ds/pkg/Icons/ArrowLeft';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { Trans } from 'react-i18next';
import { PapiCorePackage, PapiUser } from '@wix/da-papi-types';
import { type PackagePickerStoreSlice } from '../../../../redux/packagePicker/types';

import s from './BillingCyclePopup.scss';

export interface Props {
  coreType: CoreTypes;
  className?: string;
  withCloseButton?: boolean;
  isOpen?: boolean;
  onRequestClose?(): void;
  selectedPackage?: number[]; // sub product ids for coreType
  filteredPackages?: PapiCorePackage[]; // The packages for the selected sub products
  giftUser?: PapiUser;
  sale?: PackagePickerStoreSlice['sale'];
}

const BillingCyclePopup: React.FC<Props> = ({
  coreType,
  className,
  withCloseButton = false,
  isOpen,
  onRequestClose,
  filteredPackages,
  giftUser,
  sale,
}) => {
  const isMobile = useContext(MobileContext);
  const saleBackgroundUrl = sale?.saleBackgroundUrl;

  // allow the browser's back button to close the modal
  useEffect(() => {
    if (!isOpen || !onRequestClose || typeof window === 'undefined') {
      return;
    }
    window.history.pushState('modal', '', '');

    const eventListener = () => onRequestClose();
    window.addEventListener('popstate', eventListener);
    return () => {
      window.removeEventListener('popstate', eventListener);
      // clear out the pushed state if we're closing the popup
      // in some way besides the back button
      if (window.history.state === 'modal') {
        window.history.back();
      }
    };
  }, [isOpen, onRequestClose]);

  return (
    <Modal
      className={cn(s['popup'], className)}
      contentClassName={s['content']}
      overlayClassName={cn(
        !isMobile && s['overlay'],
        !isMobile && saleBackgroundUrl && s['sale-background']
      )}
      overlayStyle={{
        backgroundImage: saleBackgroundUrl
          ? `url(${saleBackgroundUrl})`
          : undefined,
      }}
      closeBtnButtonClassName={s['close-button']}
      withCloseButton={withCloseButton}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      useMobileFixedHeight={isMobile}
      forceTheme={ThemeOptions.LIGHT}
    >
      <ThemeOverride theme={ThemeOptions.LIGHT}>
        {!isMobile && (
          <div className={s['back']}>
            <a
              className={s['back-link']}
              onClick={() => onRequestClose && onRequestClose()}
            >
              <ArrowLeft
                className={s['back-icon']}
                disableDefaultColors
                size={IconSize.SMALL}
              />
              <span className={s['back-text']}>
                {/* prettier-ignore */}
                <Trans i18nKey="packagePicker.billingCycle.button.back">
                  Back to <span className={s['back-title']}>Previous</span>
                </Trans>
              </span>
            </a>
          </div>
        )}
        <div className={s['wrapper']}>
          {!isMobile && <HeroContent coreType={coreType} />}
          <BillingCyclePicker
            packages={filteredPackages || []}
            giftUser={giftUser}
            coreType={coreType}
          />
        </div>
      </ThemeOverride>
    </Modal>
  );
};
BillingCyclePopup.displayName = 'BillingCyclePopup';

export default BillingCyclePopup;
